import { createContext, useContext } from "react";

import type { CanonicalEnvironment } from "../../shared/environment";

interface AceContext {
  accountId: string | null | undefined;
  isLoggedIn?: boolean | null;
  proctorGroups?: string | null;
  buildEnvironment?: CanonicalEnvironment | null;
  /**
   * Web / embedded chat: base URL for Career Scout API (no trailing slash).
   * When set, CSAPI hooks use this instead of `getCareerScoutApiBaseUrl(buildEnvironment)`.
   */
  careerScoutApiBaseUrl?: string | null;
  /**
   * Web / embedded chat: base URL for AI Core (no trailing slash).
   * When set, AI Core callers use this instead of `getAICoreApiBaseUrl(buildEnvironment)`.
   */
  aiCoreApiBaseUrl?: string | null;
  /**
   * Web / embedded chat: optional `fetch` used for Career Scout API + AI Core HTTP.
   * When set, overrides `createNativeFetch` so the host can attach auth or call a BFF.
   */
  careerScoutFetch?: typeof fetch | null;
  /**
   * JSF UA gate for Hub W2A. True when the request device type is mobile.
   */
  isMobileDevice?: boolean;
  /**
   * Called when a Career Scout network call (career-scout-api, AI Core, or GraphQL) comes back
   * 401. The native-provided `isLoggedIn` flag can go stale (e.g. no live re-validation while
   * sitting on this tab); a real 401 is treated as ground truth and forces the sign-in screen.
   */
  markUnauthenticated: () => void;
}

export const AceContext = createContext<AceContext>({
  accountId: "",
  isLoggedIn: null,
  buildEnvironment: null,
  isMobileDevice: false,
  markUnauthenticated: () => {},
});
export const useAceContext = () => useContext(AceContext);
